import React, {
    FC, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { Dispatch } from 'redux';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
    apiGetActions,
    partsSelectActions,
} from '@app/react/action';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import { IinitState } from '@app/react/reducer';
import { OrderCommonSelectComponent, SELECT_ALL } from '@component/index';
import { OrderPartsHelpFeather } from '@component/OrderPartsHelp';
import {
    FeatherResetConfirmDialogContainer,
    FEATHERKEY,
    FeatherinsideResetConfirmDialogContainer,
    FIKEY,
} from '@container/Dialog';
import {
    filterProducts,
    reduceProductWithCategory,
    hideEcHeaderNavi,
} from '@root/app/react/common/Function';
import { buildCategoryLength } from '@app/react/common/buildCategoryLength';
import { buildSubCategoryLength } from '@app/react/common/buildSubCategoryLength';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import type { AppState } from '@app/react/reducer';
import { DesignSet, MAKIWARA, PartsProduct } from '@app/react/const/type';
import { GetResponseOrderParts } from '@app/react/api';
import { OnSelectProductHandler } from '@app/react/uikit';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { useWindowScrollPosition } from '@app/react/hooks/useWindowScrollPosition';
import { useSessionStorage } from '@app/react/hooks/useSessionStorage';
import { selectOrderPartsHelp } from './selectors';
import { isDisableFeatherinside } from '../common/isDisableFeatherinside';

const isRequireReset = (old: DesignSet, newState: PartsProduct): boolean => {
    const { familyCrestEnable = false } = old.feather?.options ?? {};
    const newFamilyCrestEnable = newState.options?.familyCrestEnable ?? false;

    if (old.feather === null) {
        return false;
    }

    if (familyCrestEnable && ((old.familycrest !== null) || (old.familycrestColor !== null))) {
        if (newFamilyCrestEnable === false) {
            return true;
        }
    }

    return false;
};

const isRequireFeatherinsideReset = (old: DesignSet, newState: PartsProduct): boolean => {
    if (old.feather === null) {
        return false;
    }

    const disableFeather = isDisableFeatherinside(newState.options);
    if (disableFeather && old.featherinside !== null) {
        return true;
    }

    return false;
};

const handleSelectProduct = (
    product: PartsProduct,
    dispatch: Dispatch,
    customizeData: DesignSet,
) => {
    const param = {
        param: product,
    };

    if (isRequireReset(customizeData, product)) {
        const meta = {
            dialogInfo: {
                key: FEATHERKEY,
                autoClose: true,
            },
        };
        dispatch(partsSelectActions.confirmFeather.started(param, meta));
        return;
    }

    dispatch(partsSelectActions.feather.started(param));
};

const handleSelectProductMakiwara = (
    product: PartsProduct,
    dispatch: Dispatch,
    customizeData: DesignSet,
) => {
    const param = {
        param: product,
    };

    if (isRequireFeatherinsideReset(customizeData, product)) {
        const meta = {
            dialogInfo: {
                key: FIKEY,
                autoClose: true,
            },
        };

        dispatch(partsSelectActions.confirmFeatherWithMakiwara.started(param, meta));
        return;
    }

    dispatch(partsSelectActions.feather.started(param));
};

export const OrderHaneSelectContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const dispatch = useDispatch();
    const { arrowType, orderType } = useOrderPathParams();
    const scrollObj = useRef(null);
    useWindowScrollPosition('scroll_feather', true, scrollObj);

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const featherProductList = useSelector<
    AppState,
    GetResponseOrderParts | null
    >((state) => state.APIData.hane);

    const openHelpParts = useSelector(selectOrderPartsHelp);
    const isOpenHelp = openHelpParts === 'feather';

    const [selectCategoryId, setSelectCategoryId] = useSessionStorage<number>(
        'cat_feather', SELECT_ALL,
    );
    const [selectSubCategoryId, setSelectSubCategoryId] = useSessionStorage<number>(
        'subcat_feather', SELECT_ALL,
    );
    const categoryChange: React.ChangeEventHandler<HTMLSelectElement> = (
        event,
    ) => {
        setSelectCategoryId(parseInt(event.target.value, 10));
        setSelectSubCategoryId(SELECT_ALL);
    };
    const subCategoryChange: React.ChangeEventHandler<HTMLSelectElement> = (
        event,
    ) => {
        setSelectSubCategoryId(parseInt(event.target.value, 10));
    };

    const categoryData = useMemo(() => (buildCategoryLength(
        featherProductList?.categoryData ?? [],
        featherProductList?.partsData ?? [],
    )), [featherProductList]);

    const subCategoryData = useMemo(() => (buildSubCategoryLength(
        featherProductList?.categoryData ?? [],
        featherProductList?.partsData ?? [],
        selectCategoryId,
    )), [featherProductList, selectCategoryId]);

    const viewData = useMemo(() => {
        const newPartsData = filterProducts(
            featherProductList?.partsData || [],
            selectCategoryId,
            selectSubCategoryId,
        );
        return reduceProductWithCategory(newPartsData);
    }, [featherProductList, selectCategoryId, selectSubCategoryId]);

    const onSelectProduct = useCallback<OnSelectProductHandler>((_, product) => {
        if (arrowType === MAKIWARA) {
            handleSelectProductMakiwara(product, dispatch, customizeData);
        } else {
            handleSelectProduct(product, dispatch, customizeData);
        }
    }, [dispatch, customizeData, arrowType]);

    const openHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: 'feather' }));
    }, [dispatch]);

    const closeHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: null }));
    }, [dispatch]);

    const isDesktop: boolean = useMediaDesktop();
    const isMobile: boolean = useMediaMobile();

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        dispatch(
            apiGetActions.feather.started({
                arrowType,
                orderType,
                selectItems: customizeData,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch, dispatch, arrowType, orderType, waitFetch]);

    return (
        <>
            <OrderCommonSelectComponent
                rootRef={scrollObj}
                categoryId={selectCategoryId}
                subCategoryId={selectSubCategoryId}
                categoryData={categoryData}
                subCategoryData={subCategoryData}
                categoryChange={categoryChange}
                subCategoryChange={subCategoryChange}
                selectedProduct={customizeData.feather}
                onSelectProduct={onSelectProduct}
                viewData={viewData}
                partsNameProps="羽根"
                showHelpBtn
                onHelpClick={openHelp}
            />
            <OrderPartsHelpFeather
                isOpen={isOpenHelp}
                handleOpen={openHelp}
                handleClose={closeHelp}
            />
            <FeatherResetConfirmDialogContainer />
            <FeatherinsideResetConfirmDialogContainer />
        </>
    );
};
